"use client"

import { useEffect, useState } from "react"
import { isTestMode } from "@ahead-health/core/system-checks/testMode"
import {
  hasShowCookieBannerOverride,
  isAnalyticsTest,
} from "@ahead-health/core/ui/cookieConsent"

/**
 * Single source of truth for "the browser test runner is driving this
 * session — hide every floating third-party widget so its chrome doesn't
 * obscure page content in screenshots / confuse the spot-check LLM."
 *
 * Most widgets are also gated at their mount site (ChatLauncher early-
 * returns in test mode; cookie-consent skips `run()`; VercelToolbarWrapper
 * skips the React `<VercelToolbar />`). The CSS rules below are belt-and-
 * braces for the cases those gates miss — chiefly Vercel's edge-injected
 * `<vercel-live-feedback>` custom element on protected previews, which
 * mounts independently of the React tree, and any widget that has already
 * created its DOM before its React component unmounts.
 *
 * Mounted unconditionally in the root layout — the `useEffect` exits as
 * a no-op when no test ID is present, so the cost in non-test sessions
 * is one mount + one state read.
 */
export function TestModeOverlaySuppressor() {
  const [suppress, setSuppress] = useState(false)

  useEffect(() => {
    if (isTestMode()) setSuppress(true)
  }, [])

  if (!suppress) return null

  // The cookie-consent tests (`?showCookieBanner=1` / `?isAnalyticsTest=1`)
  // exist to see and drive the banner — suppressing #cc-main there would hide
  // the very element the test asserts on. Every other widget stays suppressed.
  const css =
    hasShowCookieBannerOverride() || isAnalyticsTest()
      ? WIDGET_SUPPRESSION_CSS
      : `${WIDGET_SUPPRESSION_CSS}\n${COOKIE_BANNER_SUPPRESSION_CSS}`

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: css,
      }}
    />
  )
}

const WIDGET_SUPPRESSION_CSS = `
vercel-live-feedback {
  display: none !important;
}
`.trim()

const COOKIE_BANNER_SUPPRESSION_CSS = `
#cc-main {
  display: none !important;
}
`.trim()
